import {
  RULES_WITH_DEDICATED_SECTION,
  TO_CHECK_RULES_WITH_CARD,
  blockingValidationErrorsAtom,
  toCheckValidationErrorsAtom,
} from '@/states/blocking-validation-errors.state';
import {
  hasDuplicateVariantFixAtom,
  invalidGtinItemIdsAtom,
  missingVatObjectItemIdsAtom,
  missingShippingObjectItemIdsAtom,
} from '@/states/object-anomalies.state';
import {
  isPricingRule,
  pricingCorrectionGroupsAtom,
} from '@/states/pricing-correction.state';
import { psychologicalPriceErrorItemIdsAtom } from '@/states/psychological-price-errors.state';
import { atom } from 'jotai';

/**
 * Number of **blocking** anomaly types ("À corriger avant validation") surfaced
 * in the AlertSidebar: each maps to a distinct card that prevents closing the
 * offer. Blocking validation errors count once per distinct rule; the front-end
 * anomalies (missing VAT / shipping, duplicate variants) count once each when present.
 *
 * Counting **types** (not products) is intentional: a single product can carry
 * several anomalies and the "products concerned" of different types overlap, so
 * they must never be summed across types.
 */
export const blockingAnomalyTypesCountAtom = atom<number>((get) => {
  const blockingErrorsCount = get(blockingValidationErrorsAtom).filter(
    (error) =>
      !RULES_WITH_DEDICATED_SECTION.has(error.ruleId) &&
      !isPricingRule(error.ruleId)
  ).length;

  return (
    blockingErrorsCount +
    get(pricingCorrectionGroupsAtom).length +
    (get(missingVatObjectItemIdsAtom).length > 0 ? 1 : 0) +
    (get(missingShippingObjectItemIdsAtom).length > 0 ? 1 : 0) +
    (get(hasDuplicateVariantFixAtom) ? 1 : 0)
  );
});

/**
 * Number of **non-blocking** anomaly types ("À vérifier") surfaced in the
 * AlertSidebar: recommended checks that do not prevent closing the offer
 * (psychological prices, invalid GTINs). Counted by type, like
 * {@link blockingAnomalyTypesCountAtom}.
 *
 * VAT is no longer a global card: the per-product LLM check reports it as a
 * non-blocking `vat_coherence` validation error, counted here through
 * {@link TO_CHECK_RULES_WITH_CARD} — the curated whitelist of warning rules that
 * get a card, so the counter always matches the sections actually rendered.
 */
export const toCheckAnomalyTypesCountAtom = atom<number>((get) => {
  const warningErrorsCount = get(toCheckValidationErrorsAtom).filter((error) =>
    TO_CHECK_RULES_WITH_CARD.has(error.ruleId)
  ).length;

  return (
    warningErrorsCount +
    (get(psychologicalPriceErrorItemIdsAtom).length > 0 ? 1 : 0) +
    (get(invalidGtinItemIdsAtom).length > 0 ? 1 : 0)
  );
});
